import { forwardRef, useRef } from 'react';
import * as React from 'react';
import type { RenderLabel } from '@sdk/components/forms/types';
import FieldLabel from '@sdk/components/forms/components/FieldLabel';
import FieldError from '@sdk/components/forms/components/FieldError';
import Row from '@sdk/components/ui/spacing/Row';
import Chevron, { ChevronDirection } from '@sdk/components/ui/Chevron';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';

import TrashSvg from '@web/assets/svg/Icons/34/trash-gray.svg';
import {
  InputWithPointer,
  ChevronContainer,
  Container,
  InputContainer,
  TrashButton,
} from '@web/forms/components/datePicker/DatePickerCustomInput/datePickerCustomInput.styles';

type Props = {
  onClick?: () => void;
  customWidth: number;
  renderLabel: RenderLabel;
  skipErrorMessage: boolean;
  clearable: boolean;
  outputDate: string;
  invalid: boolean;
  id: string;
  label: string;
  inputPlaceholder: string;
  error: string;
  height: number;
  datepickerRef: React.MutableRefObject<any>;
  date: Date;
  onDateChange: (value: Date) => void;
  inputProps: Record<any, any>;
};

const DatePickerCustomInput = forwardRef<HTMLDivElement, Props>(
  (
    {
      height,
      onClick,
      customWidth,
      renderLabel,
      skipErrorMessage,
      clearable,
      outputDate,
      invalid,
      id,
      label,
      inputPlaceholder,
      error,
      date,
      onDateChange,
      datepickerRef,
      inputProps,
    },
    ref
  ) => {
    const chevronRef = useRef(null);
    const { disabled } = inputProps;

    const otherInputProps = {
      ...inputProps,
      placeholder: disabled ? '-' : inputProps.placeholder,
    };

    return (
      <Container ref={ref} customWidth={customWidth}>
        {label && !renderLabel && (
          <FieldLabel label={label || inputPlaceholder} htmlFor={id} invalid={invalid} />
        )}
        {renderLabel && renderLabel({ invalid, htmlFor: id })}
        <Row>
          <InputContainer>
            <InputWithPointer
              value={date ? outputDate : ''}
              onClick={onClick}
              readOnly
              height={height}
              paddingRight={chevronRef?.current?.offsetWidth}
              {...otherInputProps}
            />
            {!disabled && (
              <ChevronContainer ref={chevronRef}>
                <Chevron
                  chevronDirection={
                    datepickerRef.current?.isCalendarOpen()
                      ? ChevronDirection.up
                      : ChevronDirection.down
                  }
                />
              </ChevronContainer>
            )}
          </InputContainer>
          {date && clearable && !disabled && (
            <TrashButton
              buttonType={ButtonType.icon}
              noPadding
              iconLeft={<TrashSvg />}
              onClick={() => onDateChange(undefined)}
            />
          )}
        </Row>
        {!skipErrorMessage && <FieldError invalid={invalid} error={error} />}
      </Container>
    );
  }
);

DatePickerCustomInput.displayName = 'DatePicker_DatePickerCustomInput';

export default DatePickerCustomInput;
